import { atom, Getter, Setter } from 'jotai'
import { ExtractResumableURL } from '../data/Index'
import * as UpChunk from '@mux/upchunk'
import { db } from '../data/FirebaseUtils'
import { PostVideo, PutThumbnail, PutVideo } from '../data/Upload'
import { Keypair } from '@solana/web3.js'
import { captureException } from '@sentry/nextjs'
import { videoSupply } from '../data/VideoSupply'
import { auth } from '../data/FirebaseInit'
import { PersonaVerify } from '../data/Persona'
import log from '../events/log'
import * as CreationEvents from '../events/creation_events'

// This enum defines all the possible states of the upload process in order.
//! It is important that the order of the states is preserved.
export enum CreateSteps {
  NONE,
  ACCESS,
  FILE_UPLOAD,
  DETAILS,
  MONETIZATION,
  SCHEDULE,
  VERIFICATION,
  PROCESSING,
  PROCESSING_FAILED,
  MINT,
  COMPLETE,
}

export enum MonetizationTypes {
  RESERVE_AUCTION,
  EDITIONS,
  GUARANTEED_AUCTION,
  TIPS,
  TOKEN_GATING,
  LIQUID_EDITIONS,
}

export type MonetizationVariant = {
  title: string
  description: string
  learnMoreLink: string
  monetizationType: MonetizationTypes
}

// An object which encapsulates all the monetization options for a video.
export type MonetizationDetails = {
  pricePerEdition: number
  minNumberOfEditions: number
  maxNumberOfEditions: number
}

// Contains a mapping of the internal upload step enum to words the
// user understands.
export const UploadStepsTermMapping: { [key: number]: string } = {
  [CreateSteps.ACCESS]: 'Visibility',
  [CreateSteps.FILE_UPLOAD]: 'Upload',
  [CreateSteps.DETAILS]: 'Details',
  [CreateSteps.SCHEDULE]: 'Details',
  [CreateSteps.VERIFICATION]: 'Details',
  [CreateSteps.MONETIZATION]: 'Details',
  [CreateSteps.PROCESSING]: 'Process',
  [CreateSteps.MINT]: 'Mint',
}

export enum AccessTypes {
  GATED,
  FREE,
}

export enum MetadataErrors {
  THUMBNAIL,
  OTHER,
  NONE,
}

export type VideoMetadata = {
  title: string
  description: string
  poster: File | URL | null
}

export type VideoFileInfo = {
  url: string
  thumbnail?: string
  trailer?: string
  type: string
}

const metadataErrorAtom = atom<MetadataErrors>(MetadataErrors.NONE)

const uploadStepAtom = atom<CreateSteps>(CreateSteps.ACCESS)

export const uploadTypeAtom = atom<AccessTypes>(AccessTypes.GATED)

export const videoFileInfoAtom = atom<VideoFileInfo | null>(null)

const monetizationVariantAtom = atom<MonetizationVariant | null>(null)

const uploadFileProgressAtom = atom<number>(-1)

// Stores the video file.
const videoFileAtom = atom<File | null>(null)

export const videoMetadataAtom = atom<VideoMetadata>({
  title: '',
  description: '',
  poster: null,
})
export const draftScheduleTimeAtom = atom<Date | undefined>(undefined)
export const mintAtom = atom<Keypair | null>(null)
export const isUserVerifiedAtom = atom<boolean | null>(null)
export const rwIsTranscodingFinished = atom<boolean>(false)

const rIsMetadataReady = atom<boolean>((get) => {
  const metadata = get(videoMetadataAtom)
  return !!(metadata && metadata.title && metadata.description && metadata.poster)
})

export const rSubStepInDetail = atom<CreateSteps | undefined>((get) => {
  const isMetadataReady = get(rIsMetadataReady)
  if (!isMetadataReady) {
    return CreateSteps.DETAILS
  }

  const isMonetizationReady = get(rwMonetizationAtom)
  if (!isMonetizationReady) {
    return CreateSteps.MONETIZATION
  }

  const draftScheduleTime = get(draftScheduleTimeAtom)
  if (!draftScheduleTime) {
    return CreateSteps.SCHEDULE
  }

  const isVerified = get(isUserVerifiedAtom)
  if (!isVerified) {
    return CreateSteps.VERIFICATION
  }

  return undefined
})

// The setter functions of the atoms implicitly set the next step in the
// creation process.
export const rwUploadFileProgressAtom = atom(
  (get) => get(uploadFileProgressAtom),
  (get, set, newProgress: number) => {
    if (get(rwUploadStepAtom) === CreateSteps.COMPLETE && newProgress === 100) {
      set(uploadFileProgressAtom, -1)
    } else {
      set(uploadFileProgressAtom, newProgress)
    }
  }
)

export const rwUploadStepAtom = atom(
  (get) => get(uploadStepAtom),
  (_get, set, newStep: CreateSteps) => set(uploadStepAtom, newStep)
)

export const rwUploadTypeAtom = atom(
  (get) => get(uploadTypeAtom),
  (_get, set, type: AccessTypes) => {
    log(new CreationEvents.SelectedVisibilityEvent(type))

    set(rwUploadStepAtom, CreateSteps.FILE_UPLOAD)
    set(uploadTypeAtom, type)
  }
)

export const rwMonetizationVariantAtom = atom(
  (get) => get(monetizationVariantAtom),
  (_get, set, type: MonetizationVariant) => {
    set(monetizationVariantAtom, type)
  }
)

export const rwVideoFileAtom = atom(
  (get) => get(videoFileAtom),
  (_get, set, newFile: File) => {
    log(new CreationEvents.UploadedVideoEvent(newFile.type, newFile.size))

    set(videoFileAtom, newFile)
    set(rwUploadStepAtom, CreateSteps.DETAILS)
    const url = URL.createObjectURL(newFile)
    set(videoFileInfoAtom, {
      url,
      type: newFile.type,
    })
    set(wUploadVideoAtom)
  }
)

export const monetizationDetailsAtom = atom<MonetizationDetails | undefined>(undefined)

export const rwMonetizationAtom = atom(
  (get) => get(monetizationDetailsAtom),
  (get, set, newMonetizationDetails: MonetizationDetails) => {
    set(monetizationDetailsAtom, newMonetizationDetails)
    const mint = get(mintAtom)
    if (!mint) {
      throw new Error('Mint not found')
    }
    ;(async () => {
      try {
        log(
          new CreationEvents.SubmittedMonetizationDetailsEvent(
            newMonetizationDetails.pricePerEdition,
            newMonetizationDetails.minNumberOfEditions,
            newMonetizationDetails.maxNumberOfEditions
          )
        )

        await videoSupply(
          mint.publicKey.toString(),
          newMonetizationDetails.pricePerEdition,
          newMonetizationDetails.minNumberOfEditions,
          newMonetizationDetails.maxNumberOfEditions
        )
        set(rwUploadStepAtom, CreateSteps.SCHEDULE)
      } catch (e) {
        console.log(e)
      }
    })()
  }
)

export const rVideoFileInfoAtom = atom((get) => get(videoFileInfoAtom))

export const wUploadVideoAtom = atom(null, async (get, set) => {
  set(rwUploadFileProgressAtom, 0)

  const mint = Keypair.generate()
  set(mintAtom, mint)
  try {
    const file = get(rwVideoFileAtom)
    const details = get(videoFileInfoAtom)
    const type = get(rwUploadTypeAtom)
    if (file && details) {
      const resp = await PostVideo(mint, type === AccessTypes.GATED)
      listenToTranscodingStatus(mint.publicKey.toString(), get, set)

      const location = await ExtractResumableURL(resp.resumable_url)
      if (location) {
        const upload = UpChunk.createUpload({
          endpoint: location,
          file: file,
          chunkSize: 30720,
        })

        upload.on('error', (err) => {
          console.error('💥 🙀', err.detail)
        })

        upload.on('progress', (progress) => {
          console.log(`So far we've uploaded ${progress.detail}% of this file.`)
          set(rwUploadFileProgressAtom, progress.detail)
        })

        upload.on('success', () => {
          console.log("Wrap it up, we're done here. 👋")
          set(rwUploadFileProgressAtom, 100)
        })
      } else {
        throw new Error('no location to upload to')
      }
    }
  } catch (err) {
    console.error(err)
    set(rwUploadStepAtom, CreateSteps.PROCESSING_FAILED)
    captureException(err)
  }
})

export const rwSubmitMetadataAtom = atom(
  (get) => get(metadataErrorAtom),
  (get, set) => {
    const submit = async (): Promise<void> => {
      const mint = get(mintAtom)
      const metadata = get(videoMetadataAtom)
      if (!mint || !metadata?.poster) {
        throw new Error('no video key')
      }

      log(new CreationEvents.SubmittedVideoDetailsEvent())
      const updateTitleDescription = async (): Promise<boolean> => {
        try {
          await PutVideo(mint.publicKey.toString(), metadata.title, metadata.description)
          return true
        } catch (err) {
          console.error(err)
          set(metadataErrorAtom, MetadataErrors.OTHER)
          set(rwUploadStepAtom, CreateSteps.DETAILS)
          captureException(err)
          return false
        }
      }

      const handleMetadataUpdated = (): void => {
        set(metadataErrorAtom, MetadataErrors.NONE)
        set(rwUploadStepAtom, CreateSteps.MONETIZATION)
      }

      if (metadata.poster instanceof File) {
        const reader = new FileReader()
        reader.readAsDataURL(metadata.poster)
        reader.onload = async function () {
          if (!this.result) {
            return
          }

          const titleDescUpdated = await updateTitleDescription()
          if (!titleDescUpdated) {
            return
          }

          try {
            await PutThumbnail(mint.publicKey.toString(), this.result.toString())
          } catch (err) {
            console.error(err)
            set(metadataErrorAtom, MetadataErrors.THUMBNAIL)
            set(rwUploadStepAtom, CreateSteps.DETAILS)
            captureException(err)
            return
          }

          handleMetadataUpdated()
        }
      } else if (metadata.poster instanceof URL) {
        await updateTitleDescription()
        handleMetadataUpdated()
      }
    }
    submit()
  }
)

export const wVerifyAtom = atom(null, (get, set, inquiryId: string) => {
  const verify = async (): Promise<void> => {
    const address = auth.currentUser?.uid
    if (address) {
      try {
        await PersonaVerify(address, inquiryId)
        set(isUserVerifiedAtom, true)

        log(new CreationEvents.VerifiedKYCEvent())
      } catch (err) {
        console.log(err)
      }
      const isTranscodingFinished = get(rwIsTranscodingFinished)
      if (isTranscodingFinished) {
        set(rwUploadStepAtom, CreateSteps.MINT)
      } else {
        console.log('not finished')
        set(rwUploadStepAtom, CreateSteps.PROCESSING)
      }
    }
  }
  verify()
})

export const isUserVerified = async (): Promise<boolean> => {
  const address = auth.currentUser?.uid
  if (address) {
    const user = (await db.users.doc(address).get()).data()
    if (user) {
      return user.inquiry_id !== undefined && user.inquiry_id !== null
    }
  }
  return false
}

export const listenToTranscodingStatus = async (
  mint: string,
  get: Getter,
  set: Setter
): Promise<void> => {
  const videoData = (await db.videos.doc(mint).get()).data() || {}
  const uuid = videoData['video_uuid']

  const unsubscribe = db.transcoding.doc(uuid).onSnapshot(async (doc) => {
    const data = doc.data()

    if (data) {
      const transcoding_status = data['video_transcoding_status']
      const video_uri = data['video_uri']
      const trailer_transcoding_status = data['trailer_transcoding_status']
      const trailer_uri = data['trailer_uri']
      const thumbnail_uri = data['thumbnail_uri']

      if (
        transcoding_status === 'succeeded' &&
        video_uri !== '' &&
        trailer_transcoding_status === 'succeeded' &&
        trailer_uri !== ''
      ) {
        set(rwIsTranscodingFinished, true)
        log(new CreationEvents.ProcessedVideoEvent())

        set(videoFileInfoAtom, {
          url: video_uri,
          trailer: trailer_uri,
          thumbnail: thumbnail_uri,
          type: 'application/vnd.apple.mpegurl',
        })
        unsubscribe()

        const subStepInDetail = get(rSubStepInDetail)
        if (!subStepInDetail) {
          set(rwUploadStepAtom, CreateSteps.MINT)
        }
      } else if (transcoding_status === 'failed' || trailer_transcoding_status === 'failed') {
        set(rwUploadStepAtom, CreateSteps.PROCESSING_FAILED)
        log(new CreationEvents.ProcessingVideoFailedEvent())

        unsubscribe()
      } else if (
        transcoding_status === 'processing' ||
        trailer_transcoding_status === 'processing'
      ) {
        console.log('PROCESSING')
        // nothing
      }
    }
  })
}
